import { useEffect, useState } from "react"
import type { FormEvent } from "react"
import { Button } from "@/components/Button"
import { Skeleton } from "@/components/Skeleton/Skeleton"
import { Alert } from "@/components/Alert/Alert"
import { client } from "@/api"
import { isTrialActive } from "@/utils/subscription-helpers"

import type { OverridePlanTier } from "./override-plan-tier"

type OverrideData = {
  id?: number
  plan_tier: OverridePlanTier
  custom_price_cents?: number | null
  max_games_override?: number | null
  max_storage_gb_override?: number | null
  max_team_members_override?: number | null
  admin_notes?: string | null
}

interface SubscriptionOverrideFormProps {
  teamPublicId: string
  trialEndDate: string | null
  initialPlanTier?: OverridePlanTier
  allowTierSelection?: boolean
  embedded?: boolean
  onSaved?: () => void
}

/**
 * Manages pricing and limit overrides for a team. This works for both Stripe-backed
 * subscriptions and admin-attached team_subscriptions rows.
 */
export function SubscriptionOverrideForm({
  teamPublicId,
  trialEndDate,
  initialPlanTier = "individual",
  allowTierSelection = true,
  embedded = false,
  onSaved,
}: SubscriptionOverrideFormProps) {
  const [overrides, setOverrides] = useState<OverrideData[]>([])
  const [selectedPlanTier, setSelectedPlanTier] = useState<OverridePlanTier>(initialPlanTier)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState<{
    variant: "success" | "destructive"
    text: string
  } | null>(null)
  const [formData, setFormData] = useState<Partial<OverrideData>>({})
  const [showForm, setShowForm] = useState(false)

  const isOnTrial = isTrialActive({ trial_end_date: trialEndDate } as any)
  const currentOverride = overrides.find((o) => o.plan_tier === selectedPlanTier) || null

  useEffect(() => {
    setSelectedPlanTier(initialPlanTier)
  }, [initialPlanTier])

  useEffect(() => {
    async function fetchOverrides() {
      setIsLoading(true)
      try {
        const response = await client.admin["subscription-overrides"].team[":teamPublicId"].$get({
          param: { teamPublicId },
        })

        if (!response.ok) {
          throw new Error("Failed to fetch overrides")
        }

        const data = await response.json()
        setOverrides(data.overrides ?? [])
      } catch (error) {
        console.error("Error fetching overrides:", error)
      } finally {
        setIsLoading(false)
      }
    }

    void fetchOverrides()
  }, [teamPublicId])

  useEffect(() => {
    if (currentOverride) {
      setFormData(currentOverride)
      setShowForm(true)
    } else {
      setFormData({})
      setShowForm(false)
    }
  }, [currentOverride, selectedPlanTier])

  const overridePayload = {
    custom_price_cents: isOnTrial ? formData.custom_price_cents ?? null : null,
    max_games_override: formData.max_games_override ?? null,
    max_storage_gb_override: formData.max_storage_gb_override ?? null,
    max_team_members_override: formData.max_team_members_override ?? null,
    admin_notes: formData.admin_notes ?? null,
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    setMessage(null)

    try {
      if (currentOverride?.id) {
        const response = await client.admin["subscription-overrides"][":id"].$put({
          param: { id: currentOverride.id.toString() },
          json: overridePayload,
        })

        if (!response.ok) {
          const error: any = await response.json()
          throw new Error(error?.message || "Failed to update override")
        }

        const data = await response.json()
        setOverrides((prev) => prev.map((o) => (o.id === data.override.id ? data.override : o)))
        setMessage({
          variant: "success",
          text: `Override updated successfully for ${selectedPlanTier} plan`,
        })
      } else {
        const response = await client.admin["subscription-overrides"].$post({
          json: {
            teamPublicId,
            plan_tier: selectedPlanTier,
            ...overridePayload,
          },
        })

        if (!response.ok) {
          const error: any = await response.json()
          throw new Error(error?.message || "Failed to create override")
        }

        const data = await response.json()
        setOverrides((prev) => [...prev, data.override])
        setMessage({
          variant: "success",
          text: `Override created successfully for ${selectedPlanTier} plan`,
        })
      }

      onSaved?.()
    } catch (error) {
      console.error("Error saving override:", error)
      setMessage({
        variant: "destructive",
        text: error instanceof Error ? error.message : "Failed to save override",
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (
      !currentOverride?.id ||
      !confirm(`Are you sure you want to delete the ${selectedPlanTier} plan override?`)
    ) {
      return
    }

    setIsSaving(true)
    setMessage(null)

    try {
      const response = await client.admin["subscription-overrides"][":id"].$delete({
        param: { id: currentOverride.id.toString() },
      })

      if (!response.ok) {
        throw new Error("Failed to delete override")
      }

      setOverrides((prev) => prev.filter((o) => o.id !== currentOverride.id))
      setFormData({})
      setShowForm(false)
      setMessage({
        variant: "success",
        text: `Override deleted successfully for ${selectedPlanTier} plan`,
      })
      onSaved?.()
    } catch (error) {
      console.error("Error deleting override:", error)
      setMessage({
        variant: "destructive",
        text: error instanceof Error ? error.message : "Failed to delete override",
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div
        className={
          embedded ? "border-t border-border pt-6" : "bg-surface border border-border rounded-lg p-6"
        }
      >
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  return (
    <div
      className={
        embedded ? "border-t border-border pt-6" : "bg-surface border border-border rounded-lg p-6"
      }
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Subscription Overrides</h2>
      </div>

      {allowTierSelection && (
        <div className="flex gap-2 mb-4 border-b border-border">
          {(["individual", "team", "organization"] as const).map((tier) => {
            const hasOverride = overrides.some((o) => o.plan_tier === tier)
            return (
              <button
                key={tier}
                type="button"
                onClick={() => setSelectedPlanTier(tier)}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  selectedPlanTier === tier
                    ? "border-primary text-primary"
                    : "border-transparent text-secondary hover:text-foreground"
                }`}
              >
                {tier.charAt(0).toUpperCase() + tier.slice(1)}
                {hasOverride && <span className="ml-2 text-xs text-green-400">●</span>}
              </button>
            )
          })}
        </div>
      )}

      {message && (
        <Alert variant={message.variant} className="mb-4">
          {message.text}
        </Alert>
      )}

      {!currentOverride && !showForm && (
        <div className="text-center py-6">
          <p className="text-secondary mb-2">No override for {selectedPlanTier} plan</p>
          <p className="text-xs text-secondary mb-4">
            Limit overrides can be created anytime. Price overrides require an active trial.
          </p>
          <Button type="button" onClick={() => setShowForm(true)}>
            Create {selectedPlanTier.charAt(0).toUpperCase() + selectedPlanTier.slice(1)} Override
          </Button>
        </div>
      )}

      {(showForm || currentOverride) && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {(isOnTrial || currentOverride?.custom_price_cents != null) && (
            <div className="space-y-4">
              <h3 className="text-md font-semibold">Pricing Override</h3>
              <div>
                <label className="block text-sm font-medium mb-2">Custom Price (cents)</label>
                <input
                  type="number"
                  disabled={!isOnTrial || isSaving}
                  value={formData.custom_price_cents ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      custom_price_cents: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  placeholder="e.g., 2900 for $29.00"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground disabled:bg-muted disabled:opacity-60 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-secondary mt-1">
                  {formData.custom_price_cents
                    ? `$${(formData.custom_price_cents / 100).toFixed(2)}`
                    : !isOnTrial && currentOverride?.custom_price_cents != null
                      ? "Custom price set at checkout"
                      : "Leave empty to use plan default"}
                </p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <h3 className="text-md font-semibold">Limit Overrides</h3>
              <p className="text-xs text-secondary mt-1">
                Overrides are active and applied to all limit checks for this plan tier.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Max Games</label>
                <input
                  type="number"
                  disabled={isSaving}
                  value={formData.max_games_override ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_games_override: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  placeholder="Plan default"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Max Storage (GB)</label>
                <input
                  type="number"
                  disabled={isSaving}
                  value={formData.max_storage_gb_override ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_storage_gb_override: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  placeholder="Plan default"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Max Team Members</label>
                <input
                  type="number"
                  disabled={isSaving}
                  value={formData.max_team_members_override ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_team_members_override: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  placeholder="Plan default"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Admin Notes</label>
            <textarea
              disabled={isSaving}
              value={formData.admin_notes ?? ""}
              onChange={(e) => setFormData({ ...formData, admin_notes: e.target.value || null })}
              placeholder="Internal notes about this override..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            />
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div>
              {currentOverride && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isSaving}
                >
                  Delete Override
                </Button>
              )}
            </div>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : currentOverride ? "Update Override" : "Create Override"}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
